import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from 'react'
import {
  calculateResponsivePadding,
  fitToContent as calculateFitToContent,
  measureLayerContentBounds,
  type ContentBounds,
} from './fitToContent'
import { usePersistedState } from '../../hooks/usePersistedState'
import type { CanvasAxis, ZoomMode } from '../Dashboard/viewDescriptors'

export type PannableCanvasTransform = {
  x: number
  y: number
  zoom: number
}

export type PannableCanvasGestureState = {
  isPanning: boolean
}

export const PANNABLE_CANVAS_DEFAULT_TRANSFORM: PannableCanvasTransform = {
  x: 0,
  y: 0,
  zoom: 1,
}

export const PANNABLE_CANVAS_MIN_ZOOM = 0.25
export const PANNABLE_CANVAS_ABSOLUTE_MIN_ZOOM = 0.01
export const PANNABLE_CANVAS_MAX_ZOOM = 4
export const PANNABLE_CANVAS_ZOOM_FACTOR = 1.2
export const PANNABLE_CANVAS_PAN_THRESHOLD_PX = 3
export const PANNABLE_CANVAS_PERSIST_DELAY_MS = 250
export const RUBBER_BAND_RESISTANCE = 0.55
export const MOMENTUM_FRICTION = 0.94
export const MOMENTUM_VELOCITY_WINDOW_MS = 100

type ActivePointer = {
  x: number
  y: number
  pointerType: string
}

type PanGesture = {
  type: 'pan'
  pointerId: number
  startX: number
  startY: number
  baseTransform: PannableCanvasTransform
  started: boolean
  moved: boolean
}

type PinchGesture = {
  type: 'pinch'
  pointerIds: [number, number]
  startMidpoint: Point
  startDistance: number
  lastDistance: number
  contentAnchor: Point
  baseTransform: PannableCanvasTransform
  moved: boolean
}

type Gesture = PanGesture | PinchGesture | null

export type Point = {
  x: number
  y: number
}

type VelocitySample = {
  x: number
  y: number
  time: number
}

const finiteOr = (value: unknown, fallback: number): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback

export const clampPannableCanvasZoom = (zoom: number): number =>
  Math.min(PANNABLE_CANVAS_MAX_ZOOM, Math.max(PANNABLE_CANVAS_MIN_ZOOM, zoom))

export const clampPannableCanvasZoomWithFloor = (zoom: number, minZoom: number): number =>
  Math.min(PANNABLE_CANVAS_MAX_ZOOM, Math.max(minZoom, zoom))

export const applyRubberBand = (
  value: number,
  min: number,
  max: number,
  resistance = RUBBER_BAND_RESISTANCE,
): number => {
  if (value < min) return min - (min - value) * resistance
  if (value > max) return max + (value - max) * resistance
  return value
}

export const calculateMomentumStep = (
  velocity: Point,
  friction: number,
  deltaMs: number,
): Point => {
  const decay = Math.pow(friction, deltaMs / 16)
  return {
    x: velocity.x * decay,
    y: velocity.y * decay,
  }
}

export const getPannableCanvasStorageKey = (workspaceId: string, viewId: string): string =>
  `dashboard.pan.${workspaceId}.${viewId}`

export const normalizePannableCanvasTransform = (
  value: unknown,
  minZoom = PANNABLE_CANVAS_ABSOLUTE_MIN_ZOOM,
): PannableCanvasTransform => {
  if (!value || typeof value !== 'object') return PANNABLE_CANVAS_DEFAULT_TRANSFORM
  const candidate = value as Partial<PannableCanvasTransform>
  return {
    x: finiteOr(candidate.x, PANNABLE_CANVAS_DEFAULT_TRANSFORM.x),
    y: finiteOr(candidate.y, PANNABLE_CANVAS_DEFAULT_TRANSFORM.y),
    zoom: clampPannableCanvasZoomWithFloor(
      finiteOr(candidate.zoom, PANNABLE_CANVAS_DEFAULT_TRANSFORM.zoom),
      minZoom,
    ),
  }
}

export const isEditablePannableCanvasTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false
  const editable = target.closest('input, textarea, select, [contenteditable=""], [contenteditable="true"]')
  return Boolean(editable)
}

export const isInteractiveOrIgnoredTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false
  return Boolean(
    target.closest(
      'input, textarea, select, [contenteditable=""], [contenteditable="true"], [data-no-dashboard-drag]',
    ),
  )
}

const distance = (a: Point, b: Point): number => Math.hypot(a.x - b.x, a.y - b.y)

const midpoint = (a: Point, b: Point): Point => ({
  x: (a.x + b.x) / 2,
  y: (a.y + b.y) / 2,
})

export const transformAboutPoint = (
  transform: PannableCanvasTransform,
  nextZoom: number,
  point: Point,
  zoomAxis: CanvasAxis = 'xy',
): PannableCanvasTransform => {
  const ratio = nextZoom / transform.zoom
  const xRatio = zoomAxis === 'y' || zoomAxis === 'none' ? 1 : ratio
  const yRatio = zoomAxis === 'x' || zoomAxis === 'none' ? 1 : ratio
  return {
    x: point.x - (point.x - transform.x) * xRatio,
    y: point.y - (point.y - transform.y) * yRatio,
    zoom: nextZoom,
  }
}

const transformAboutViewportCenter = (
  transform: PannableCanvasTransform,
  zoomFactor: number,
  viewport: HTMLElement | null,
  minZoom = PANNABLE_CANVAS_MIN_ZOOM,
  zoomAxis: CanvasAxis = 'xy',
): PannableCanvasTransform => {
  const nextZoom = clampPannableCanvasZoomWithFloor(transform.zoom * zoomFactor, minZoom)
  const rect = viewport?.getBoundingClientRect()
  const centerX = rect ? rect.width / 2 : 0
  const centerY = rect ? rect.height / 2 : 0
  return transformAboutPoint(transform, nextZoom, { x: centerX, y: centerY }, zoomAxis)
}

export const buildPannableCanvasTransformStyle = (
  transform: PannableCanvasTransform,
  zoomAxis: CanvasAxis = 'xy',
): string => {
  if (zoomAxis === 'x') {
    return `translate3d(${transform.x}px, ${transform.y}px, 0) scale(${transform.zoom}, 1)`
  }
  if (zoomAxis === 'y') {
    return `translate3d(${transform.x}px, ${transform.y}px, 0) scale(1, ${transform.zoom})`
  }
  if (zoomAxis === 'none') {
    return `translate3d(${transform.x}px, ${transform.y}px, 0)`
  }
  return `translate3d(${transform.x}px, ${transform.y}px, 0) scale(${transform.zoom})`
}

const capturePointer = (target: HTMLElement, pointerId: number) => {
  try {
    target.setPointerCapture?.(pointerId)
  } catch {
    // Synthetic/browser-automation pointer events may not be active pointers.
  }
}

export const applyRubberBandPan = (
  transform: PannableCanvasTransform,
  bounds: ContentBounds | null,
  viewport: DOMRect | null,
  resistance = RUBBER_BAND_RESISTANCE,
  pan: CanvasAxis = 'xy',
  baseTransform?: PannableCanvasTransform,
): PannableCanvasTransform => {
  if (!bounds || !viewport) return transform
  const padding = calculateResponsivePadding(viewport.width)
  const minX = Math.min(viewport.width - padding - bounds.right * transform.zoom, padding - bounds.left * transform.zoom)
  const maxX = Math.max(viewport.width - padding - bounds.right * transform.zoom, padding - bounds.left * transform.zoom)
  const minY = Math.min(viewport.height - padding - bounds.bottom * transform.zoom, padding - bounds.top * transform.zoom)
  const maxY = Math.max(viewport.height - padding - bounds.bottom * transform.zoom, padding - bounds.top * transform.zoom)

  const baseX = baseTransform?.x ?? transform.x
  const baseY = baseTransform?.y ?? transform.y

  return {
    x: pan === 'none' || pan === 'y' ? baseX : applyRubberBand(transform.x, minX, maxX, resistance),
    y: pan === 'none' || pan === 'x' ? baseY : applyRubberBand(transform.y, minY, maxY, resistance),
    zoom: transform.zoom,
  }
}

export const clampPanToBounds = (
  transform: PannableCanvasTransform,
  bounds: ContentBounds | null,
  viewport: DOMRect | null,
  pan: CanvasAxis = 'xy',
  baseTransform?: PannableCanvasTransform,
): PannableCanvasTransform => {
  if (!bounds || !viewport) return transform
  const padding = calculateResponsivePadding(viewport.width)
  const minX = Math.min(viewport.width - padding - bounds.right * transform.zoom, padding - bounds.left * transform.zoom)
  const maxX = Math.max(viewport.width - padding - bounds.right * transform.zoom, padding - bounds.left * transform.zoom)
  const minY = Math.min(viewport.height - padding - bounds.bottom * transform.zoom, padding - bounds.top * transform.zoom)
  const maxY = Math.max(viewport.height - padding - bounds.bottom * transform.zoom, padding - bounds.top * transform.zoom)

  const baseX = baseTransform?.x ?? transform.x
  const baseY = baseTransform?.y ?? transform.y

  return {
    x: pan === 'none' || pan === 'y' ? baseX : Math.min(maxX, Math.max(minX, transform.x)),
    y: pan === 'none' || pan === 'x' ? baseY : Math.min(maxY, Math.max(minY, transform.y)),
    zoom: transform.zoom,
  }
}

export function usePannableCanvasState({
  viewId,
  workspaceId,
  viewportRef,
  layerRef,
  pan = 'xy',
  zoomAxis = 'xy',
  zoomMode,
  onSemanticZoom,
}: {
  viewId: string
  workspaceId: string
  viewportRef: RefObject<HTMLElement | null>
  layerRef: RefObject<HTMLElement | null>
  pan?: CanvasAxis
  zoomAxis?: CanvasAxis
  zoomMode?: ZoomMode
  onSemanticZoom?: (anchorRatio: number, zoomFactor: number) => void
}) {
  const storageKey = useMemo(() => getPannableCanvasStorageKey(workspaceId, viewId), [viewId, workspaceId])
  const [persistedTransform, setPersistedTransform] = usePersistedState<PannableCanvasTransform>(
    storageKey,
    PANNABLE_CANVAS_DEFAULT_TRANSFORM,
    {
      storage: 'local',
      scope: 'none',
      writeDelayMs: PANNABLE_CANVAS_PERSIST_DELAY_MS,
      skipInitialWrite: true,
    },
  )
  const [transform, setTransform] = useState<PannableCanvasTransform>(() => {
    const norm = normalizePannableCanvasTransform(persistedTransform)
    return zoomMode === 'semantic' ? { ...norm, zoom: 1 } : norm
  })
  const [isPanning, setIsPanning] = useState(false)
  const didDragRef = useRef(false)
  const transformRef = useRef(transform)
  const pointersRef = useRef(new Map<number, ActivePointer>())
  const gestureRef = useRef<Gesture>(null)
  const velocityHistoryRef = useRef<VelocitySample[]>([])
  const momentumRafRef = useRef<number | null>(null)
  const springBackRafRef = useRef<number | null>(null)
  const rafWriteIdRef = useRef<number | null>(null)
  const pendingTransformRef = useRef<PannableCanvasTransform | null>(null)
  const lastTapRef = useRef<{ x: number; y: number; time: number } | null>(null)
  const twoFingerStartRef = useRef<{ time: number; moved: boolean; midpoint: Point } | null>(null)
  const isNormalReleaseRef = useRef(false)
  const pendingReconcileRef = useRef(false)

  const [prevPersisted, setPrevPersisted] = useState(persistedTransform)
  if (prevPersisted !== persistedTransform) {
    setPrevPersisted(persistedTransform)
    const norm = normalizePannableCanvasTransform(persistedTransform)
    const safe = zoomMode === 'semantic' ? { ...norm, zoom: 1 } : norm
    setTransform(safe)
  }

  useEffect(() => {
    transformRef.current = transform
  }, [transform])

  const cancelActiveAnimations = useCallback(() => {
    if (momentumRafRef.current !== null) {
      cancelAnimationFrame(momentumRafRef.current)
      momentumRafRef.current = null
    }
    if (springBackRafRef.current !== null) {
      cancelAnimationFrame(springBackRafRef.current)
      springBackRafRef.current = null
    }
  }, [])

  // Clean up all animation frames on unmount
  useEffect(() => {
    return () => {
      if (momentumRafRef.current !== null) cancelAnimationFrame(momentumRafRef.current)
      if (springBackRafRef.current !== null) cancelAnimationFrame(springBackRafRef.current)
      if (rafWriteIdRef.current !== null) cancelAnimationFrame(rafWriteIdRef.current)
    }
  }, [])

  const commitTransform = useCallback((next: PannableCanvasTransform, minZoom = PANNABLE_CANVAS_ABSOLUTE_MIN_ZOOM) => {
    cancelActiveAnimations()
    if (rafWriteIdRef.current !== null) {
      cancelAnimationFrame(rafWriteIdRef.current)
      rafWriteIdRef.current = null
    }
    const safeNext = zoomMode === 'semantic' ? { ...next, zoom: 1 } : next
    const normalized = normalizePannableCanvasTransform(safeNext, minZoom)
    transformRef.current = normalized
    setTransform(normalized)
    setPersistedTransform(normalized)
  }, [cancelActiveAnimations, setPersistedTransform, zoomMode])

  const scheduleTransformWrite = useCallback((next: PannableCanvasTransform) => {
    const safeNext = zoomMode === 'semantic' ? { ...next, zoom: 1 } : next
    transformRef.current = safeNext
    pendingTransformRef.current = safeNext
    if (rafWriteIdRef.current === null) {
      rafWriteIdRef.current = requestAnimationFrame(() => {
        rafWriteIdRef.current = null
        if (pendingTransformRef.current) {
          setTransform(pendingTransformRef.current)
        }
      })
    }
  }, [zoomMode])

  const flushTransformWrite = useCallback(() => {
    if (rafWriteIdRef.current !== null) {
      cancelAnimationFrame(rafWriteIdRef.current)
      rafWriteIdRef.current = null
    }
    setTransform(transformRef.current)
  }, [])

  const getViewportRect = useCallback(() => viewportRef.current?.getBoundingClientRect() ?? null, [viewportRef])

  const getFitTransform = useCallback((minZoom = 0) => {
    const layer = layerRef.current
    const viewportRect = getViewportRect()
    if (!layer || !viewportRect) return PANNABLE_CANVAS_DEFAULT_TRANSFORM
    const current = transformRef.current
    const fit = calculateFitToContent(layer, viewportRect, {
      minZoom,
      maxZoom: PANNABLE_CANVAS_MAX_ZOOM,
      zoomAxis,
      zoomMode,
    })
    if (pan === 'none' || pan === 'y') {
      fit.x = current.x
    }
    if (pan === 'none' || pan === 'x') {
      fit.y = current.y
    }
    return fit
  }, [getViewportRect, layerRef, pan, zoomAxis, zoomMode])

  const getDynamicMinZoom = useCallback(() => {
    const layer = layerRef.current
    const viewportRect = getViewportRect()
    if (!layer || !viewportRect) return PANNABLE_CANVAS_MIN_ZOOM
    const fitTransform = calculateFitToContent(layer, viewportRect, {
      minZoom: 0,
      maxZoom: PANNABLE_CANVAS_MAX_ZOOM,
      zoomAxis,
      zoomMode,
    })
    return Math.min(PANNABLE_CANVAS_MIN_ZOOM, fitTransform.zoom)
  }, [getViewportRect, layerRef, zoomAxis, zoomMode])

  const centerContentIfZoomedPastFit = useCallback((
    candidate: PannableCanvasTransform,
    easing: number,
  ): PannableCanvasTransform => {
    const layer = layerRef.current
    const viewportRect = getViewportRect()
    if (!layer || !viewportRect) return candidate
    const bounds = measureLayerContentBounds(layer)
    if (!bounds) return candidate
    const fitTransform = calculateFitToContent(layer, viewportRect, {
      minZoom: 0,
      maxZoom: PANNABLE_CANVAS_MAX_ZOOM,
      zoomAxis,
      zoomMode,
    })
    if (candidate.zoom >= fitTransform.zoom) return candidate

    const target = {
      x: viewportRect.width / 2 - bounds.centerX * candidate.zoom,
      y: viewportRect.height / 2 - bounds.centerY * candidate.zoom,
    }
    return {
      x: candidate.x + (target.x - candidate.x) * easing,
      y: candidate.y + (target.y - candidate.y) * easing,
      zoom: candidate.zoom,
    }
  }, [getViewportRect, layerRef, zoomAxis, zoomMode])

  const reducedMotionPreferred = useCallback(
    () =>
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    [],
  )

  const zoomBy = useCallback((factor: number) => {
    if (zoomMode === 'density' || zoomMode === 'none') return
    if (zoomMode === 'semantic') {
      onSemanticZoom?.(0.5, 1 / factor)
      return
    }
    if (zoomAxis === 'none') return
    const dynamicMinZoom = getDynamicMinZoom()
    const centered = transformAboutViewportCenter(
      transformRef.current,
      factor,
      viewportRef.current,
      dynamicMinZoom,
      zoomAxis,
    )
    const next = factor < 1
      ? centerContentIfZoomedPastFit(centered, reducedMotionPreferred() ? 1 : 0.35)
      : centered
    commitTransform(next, dynamicMinZoom)
  }, [centerContentIfZoomedPastFit, commitTransform, getDynamicMinZoom, onSemanticZoom, reducedMotionPreferred, viewportRef, zoomAxis, zoomMode])

  const zoomIn = useCallback(() => {
    if (zoomMode === 'density' || zoomMode === 'none') return
    if (zoomMode === 'semantic') {
      onSemanticZoom?.(0.5, 1 / PANNABLE_CANVAS_ZOOM_FACTOR)
      return
    }
    zoomBy(PANNABLE_CANVAS_ZOOM_FACTOR)
  }, [onSemanticZoom, zoomBy, zoomMode])

  const zoomOut = useCallback(() => {
    if (zoomMode === 'density' || zoomMode === 'none') return
    if (zoomMode === 'semantic') {
      onSemanticZoom?.(0.5, PANNABLE_CANVAS_ZOOM_FACTOR)
      return
    }
    zoomBy(1 / PANNABLE_CANVAS_ZOOM_FACTOR)
  }, [onSemanticZoom, zoomBy, zoomMode])
  const fitToContent = useCallback(() => commitTransform(getFitTransform(), PANNABLE_CANVAS_ABSOLUTE_MIN_ZOOM), [commitTransform, getFitTransform])

  const panBy = useCallback((deltaX: number, deltaY: number) => {
    const effectiveDx = pan === 'none' || pan === 'y' ? 0 : deltaX
    const effectiveDy = pan === 'none' || pan === 'x' ? 0 : deltaY
    if (effectiveDx === 0 && effectiveDy === 0) return
    const current = transformRef.current
    const nextTransform = {
      x: current.x + effectiveDx,
      y: current.y + effectiveDy,
      zoom: current.zoom,
    }
    commitTransform(nextTransform)
  }, [commitTransform, pan])

  const zoomAt = useCallback((anchorX: number, anchorY: number, factor: number) => {
    if (zoomMode === 'density' || zoomMode === 'none') return
    if (zoomMode === 'semantic') {
      const viewport = getViewportRect()
      const width = Math.max(1, viewport?.width ?? window.innerWidth)
      const anchorRatio = Math.min(1, Math.max(0, (anchorX - (viewport?.left ?? 0)) / width))
      onSemanticZoom?.(anchorRatio, 1 / factor)
      return
    }
    if (zoomAxis === 'none') return
    const dynamicMinZoom = getDynamicMinZoom()
    const current = transformRef.current
    const targetZoom = clampPannableCanvasZoomWithFloor(current.zoom * factor, dynamicMinZoom)
    const viewport = getViewportRect()
    const localAnchor = {
      x: anchorX - (viewport?.left ?? 0),
      y: anchorY - (viewport?.top ?? 0),
    }
    const nextTransform = transformAboutPoint(current, targetZoom, localAnchor, zoomAxis)
    commitTransform(nextTransform, dynamicMinZoom)
  }, [commitTransform, getDynamicMinZoom, getViewportRect, onSemanticZoom, zoomAxis, zoomMode])

  const reconcileDimensions = useCallback(() => {
    if (
      gestureRef.current !== null ||
      pointersRef.current.size > 0 ||
      momentumRafRef.current !== null ||
      springBackRafRef.current !== null
    ) {
      pendingReconcileRef.current = true
      return
    }
    const layer = layerRef.current
    const viewportRect = getViewportRect()
    if (!layer || !viewportRect || viewportRect.width <= 0 || viewportRect.height <= 0) return
    const bounds = measureLayerContentBounds(layer)
    if (!bounds) return
    const current = transformRef.current
    const clamped = clampPanToBounds(current, bounds, viewportRect, pan, current)
    if (Math.abs(clamped.x - current.x) > 1 || Math.abs(clamped.y - current.y) > 1) {
      commitTransform(clamped)
    }
  }, [commitTransform, getViewportRect, layerRef, pan])

  const springBackIfNeeded = useCallback(() => {
    const current = transformRef.current
    const dynamicMinZoom = getDynamicMinZoom()
    const targetZoom = clampPannableCanvasZoomWithFloor(current.zoom, dynamicMinZoom)
    const layer = layerRef.current
    const bounds = layer ? measureLayerContentBounds(layer) : null
    const viewportRect = getViewportRect()
    const clampedPan = clampPanToBounds({ ...current, zoom: targetZoom }, bounds, viewportRect, pan, current)

    const needsZoomSpring = Math.abs(current.zoom - targetZoom) > 0.001
    const needsPanSpring = Math.abs(current.x - clampedPan.x) > 0.5 || Math.abs(current.y - clampedPan.y) > 0.5

    if (!needsZoomSpring && !needsPanSpring) {
      if (pendingReconcileRef.current) {
        pendingReconcileRef.current = false
        reconcileDimensions()
      }
      return
    }

    if (springBackRafRef.current !== null) cancelAnimationFrame(springBackRafRef.current)

    let stepCount = 0
    const maxSteps = 12

    const runSpring = () => {
      stepCount += 1
      const cur = transformRef.current
      const t = stepCount / maxSteps
      // Smooth cubic out easing
      const ease = 1 - Math.pow(1 - t, 3)

      const next = {
        x: cur.x + (clampedPan.x - cur.x) * ease,
        y: cur.y + (clampedPan.y - cur.y) * ease,
        zoom: cur.zoom + (targetZoom - cur.zoom) * ease,
      }

      scheduleTransformWrite(next)

      if (stepCount < maxSteps) {
        springBackRafRef.current = requestAnimationFrame(runSpring)
      } else {
        springBackRafRef.current = null
        commitTransform(clampedPan, dynamicMinZoom)
        if (pendingReconcileRef.current) {
          pendingReconcileRef.current = false
          reconcileDimensions()
        }
      }
    }

    springBackRafRef.current = requestAnimationFrame(runSpring)
  }, [commitTransform, getDynamicMinZoom, getViewportRect, layerRef, pan, reconcileDimensions, scheduleTransformWrite])

  const cancelGesture = useCallback(() => {
    isNormalReleaseRef.current = false
    pointersRef.current.clear()
    gestureRef.current = null
    twoFingerStartRef.current = null
    velocityHistoryRef.current = []
    pendingReconcileRef.current = false
    if (rafWriteIdRef.current !== null) {
      cancelAnimationFrame(rafWriteIdRef.current)
      rafWriteIdRef.current = null
    }
    cancelActiveAnimations()
    flushTransformWrite()
    springBackIfNeeded()
    setIsPanning(false)
    didDragRef.current = false
    const viewport = viewportRef.current
    if (viewport) {
      viewport.removeAttribute('data-pannable-canvas-panning')
      viewport.removeAttribute('data-pannable-canvas-dragged')
    }
  }, [cancelActiveAnimations, flushTransformWrite, springBackIfNeeded, viewportRef])

  useEffect(() => {
    const handleBlur = () => {
      cancelGesture()
    }
    window.addEventListener('blur', handleBlur)
    return () => window.removeEventListener('blur', handleBlur)
  }, [cancelGesture])

  const startMomentum = useCallback((initialVx: number, initialVy: number) => {
    let vx = initialVx
    let vy = initialVy
    let lastTime = performance.now()

    if (momentumRafRef.current !== null) cancelAnimationFrame(momentumRafRef.current)

    const runMomentum = () => {
      const now = performance.now()
      const deltaMs = Math.min(32, Math.max(1, now - lastTime))
      lastTime = now

      const nextVel = calculateMomentumStep({ x: vx, y: vy }, MOMENTUM_FRICTION, deltaMs)
      vx = nextVel.x
      vy = nextVel.y

      const current = transformRef.current
      const nextTransform = {
        x: pan === 'none' || pan === 'y' ? current.x : current.x + vx * deltaMs,
        y: pan === 'none' || pan === 'x' ? current.y : current.y + vy * deltaMs,
        zoom: current.zoom,
      }

      scheduleTransformWrite(nextTransform)

      const speed = Math.hypot(vx, vy)
      if (speed > 0.01) {
        momentumRafRef.current = requestAnimationFrame(runMomentum)
      } else {
        momentumRafRef.current = null
        gestureRef.current = null
        springBackIfNeeded()
        setPersistedTransform(normalizePannableCanvasTransform(transformRef.current))
        setIsPanning(false)
        if (pendingReconcileRef.current) {
          pendingReconcileRef.current = false
          reconcileDimensions()
        }
      }
    }

    momentumRafRef.current = requestAnimationFrame(runMomentum)
  }, [pan, reconcileDimensions, scheduleTransformWrite, setPersistedTransform, springBackIfNeeded])

  const startPinchIfReady = useCallback(() => {
    const touchPointers = [...pointersRef.current.entries()].filter(([, pointer]) => pointer.pointerType === 'touch')
    if (touchPointers.length < 2) return false
    const [[firstId, first], [secondId, second]] = touchPointers
    const firstPoint = { x: first.x, y: first.y }
    const secondPoint = { x: second.x, y: second.y }
    const startMidpoint = midpoint(firstPoint, secondPoint)
    const initialDistance = Math.max(1, distance(firstPoint, secondPoint))
    const viewport = getViewportRect()
    const viewportLeft = viewport?.left ?? 0
    const viewportTop = viewport?.top ?? 0
    const localStartMidpoint = {
      x: startMidpoint.x - viewportLeft,
      y: startMidpoint.y - viewportTop,
    }
    gestureRef.current = {
      type: 'pinch',
      pointerIds: [firstId, secondId],
      startMidpoint,
      startDistance: initialDistance,
      lastDistance: initialDistance,
      contentAnchor: {
        x: (localStartMidpoint.x - transformRef.current.x) / transformRef.current.zoom,
        y: (localStartMidpoint.y - transformRef.current.y) / transformRef.current.zoom,
      },
      baseTransform: transformRef.current,
      moved: false,
    }
    twoFingerStartRef.current = {
      time: performance.now(),
      moved: false,
      midpoint: startMidpoint,
    }
    setIsPanning(true)
    return true
  }, [getViewportRect])

  const onPointerDown = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    if (pan === 'none') return
    if (isInteractiveOrIgnoredTarget(event.target)) return
    if (event.button !== 0) return

    cancelActiveAnimations()

    pointersRef.current.set(event.pointerId, {
      x: event.clientX,
      y: event.clientY,
      pointerType: event.pointerType,
    })

    if (event.pointerType === 'touch' && startPinchIfReady()) {
      event.preventDefault()
      capturePointer(event.currentTarget, event.pointerId)
      return
    }

    // Double-tap zoom detection
    const now = performance.now()
    const lastTap = lastTapRef.current
    if (
      lastTap &&
      now - lastTap.time < 300 &&
      Math.hypot(event.clientX - lastTap.x, event.clientY - lastTap.y) < 25 &&
      zoomAxis !== 'none'
    ) {
      lastTapRef.current = null
      event.preventDefault()
      if (zoomMode === 'density' || zoomMode === 'none') {
        return
      }
      const viewport = getViewportRect()
      if (zoomMode === 'semantic') {
        const width = Math.max(1, viewport?.width ?? window.innerWidth)
        const anchorRatio = Math.min(1, Math.max(0, (event.clientX - (viewport?.left ?? 0)) / width))
        onSemanticZoom?.(anchorRatio, 1 / 1.5)
        return
      }
      const localAnchor = {
        x: event.clientX - (viewport?.left ?? 0),
        y: event.clientY - (viewport?.top ?? 0),
      }
      const targetZoom = clampPannableCanvasZoom(transformRef.current.zoom * 1.5)
      const nextTransform = transformAboutPoint(
        transformRef.current,
        targetZoom,
        localAnchor,
        zoomAxis,
      )
      commitTransform(nextTransform)
      return
    }
    lastTapRef.current = { x: event.clientX, y: event.clientY, time: now }

    // Prepare pan gesture without capturing or preventing default until movement threshold is reached
    gestureRef.current = {
      type: 'pan',
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      baseTransform: transformRef.current,
      started: false,
      moved: false,
    }
    velocityHistoryRef.current = [{ x: event.clientX, y: event.clientY, time: now }]
  }, [cancelActiveAnimations, commitTransform, getViewportRect, onSemanticZoom, pan, startPinchIfReady, zoomAxis, zoomMode])

  const onPointerMove = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    const currentPointer = pointersRef.current.get(event.pointerId)
    if (currentPointer) {
      pointersRef.current.set(event.pointerId, {
        ...currentPointer,
        x: event.clientX,
        y: event.clientY,
      })
    }

    const gesture = gestureRef.current
    if (!gesture) return

    if (gesture.type === 'pinch') {
      const [first, second] = gesture.pointerIds.map((id) => pointersRef.current.get(id))
      if (!first || !second) return
      const firstPoint = { x: first.x, y: first.y }
      const secondPoint = { x: second.x, y: second.y }
      const nextMidpoint = midpoint(firstPoint, secondPoint)
      const currentDistance = distance(firstPoint, secondPoint)
      if (Math.abs(currentDistance - gesture.startDistance) > PANNABLE_CANVAS_PAN_THRESHOLD_PX) {
        if (twoFingerStartRef.current) twoFingerStartRef.current.moved = true
      }

      if (zoomMode === 'density' || zoomMode === 'none') {
        const dx = nextMidpoint.x - gesture.startMidpoint.x
        const dy = nextMidpoint.y - gesture.startMidpoint.y
        const effectiveDx = pan === 'none' || pan === 'y' ? 0 : dx
        const effectiveDy = pan === 'none' || pan === 'x' ? 0 : dy
        const rawTransform: PannableCanvasTransform = {
          x: gesture.baseTransform.x + effectiveDx,
          y: gesture.baseTransform.y + effectiveDy,
          zoom: gesture.baseTransform.zoom,
        }
        const bounds = layerRef.current ? measureLayerContentBounds(layerRef.current) : null
        const viewport = getViewportRect()
        const rubberBanded = applyRubberBandPan(rawTransform, bounds, viewport)
        gestureRef.current = { ...gesture, moved: true, lastDistance: currentDistance }
        scheduleTransformWrite(rubberBanded)
        event.preventDefault()
        return
      }

      if (zoomMode === 'semantic') {
        const viewport = getViewportRect()
        const width = Math.max(1, viewport?.width ?? window.innerWidth)
        const anchorRatio = Math.min(1, Math.max(0, (nextMidpoint.x - (viewport?.left ?? 0)) / width))

        if (gesture.lastDistance > 0 && Math.abs(currentDistance - gesture.lastDistance) > 1) {
          const zoomFactor = gesture.lastDistance / currentDistance
          onSemanticZoom?.(anchorRatio, zoomFactor)
        }

        const dx = nextMidpoint.x - gesture.startMidpoint.x
        const dy = nextMidpoint.y - gesture.startMidpoint.y
        const effectiveDx = pan === 'none' || pan === 'y' ? 0 : dx
        const effectiveDy = pan === 'none' || pan === 'x' ? 0 : dy
        const rawTransform: PannableCanvasTransform = {
          x: gesture.baseTransform.x + effectiveDx,
          y: gesture.baseTransform.y + effectiveDy,
          zoom: 1,
        }
        const bounds = layerRef.current ? measureLayerContentBounds(layerRef.current) : null
        const rubberBanded = applyRubberBandPan(rawTransform, bounds, viewport)
        gestureRef.current = {
          ...gesture,
          moved: true,
          lastDistance: currentDistance,
        }
        scheduleTransformWrite(rubberBanded)
        event.preventDefault()
        return
      }

      if (zoomAxis === 'none') return

      const zoomRatio = currentDistance / gesture.startDistance
      const rawZoom = gesture.baseTransform.zoom * zoomRatio
      const dynamicMinZoom = getDynamicMinZoom()
      const softZoom = applyRubberBand(rawZoom, dynamicMinZoom, PANNABLE_CANVAS_MAX_ZOOM, RUBBER_BAND_RESISTANCE)

      const viewport = getViewportRect()
      const localNextMidpoint = {
        x: nextMidpoint.x - (viewport?.left ?? 0),
        y: nextMidpoint.y - (viewport?.top ?? 0),
      }
      const nextTransform = transformAboutPoint(gesture.baseTransform, softZoom, localNextMidpoint, zoomAxis)
      gestureRef.current = { ...gesture, moved: true, lastDistance: currentDistance }
      scheduleTransformWrite(nextTransform)
      event.preventDefault()
      return
    }

    if (gesture.pointerId !== event.pointerId) return
    const dx = event.clientX - gesture.startX
    const dy = event.clientY - gesture.startY
    const effectiveDx = pan === 'none' || pan === 'y' ? 0 : dx
    const effectiveDy = pan === 'none' || pan === 'x' ? 0 : dy

    if (!gesture.started) {
      if (Math.hypot(effectiveDx, effectiveDy) < PANNABLE_CANVAS_PAN_THRESHOLD_PX) return
      capturePointer(event.currentTarget, event.pointerId)
      const viewport = viewportRef.current
      if (viewport) {
        viewport.setAttribute('data-pannable-canvas-panning', 'true')
        viewport.setAttribute('data-pannable-canvas-dragged', 'true')
      }
    }

    const now = performance.now()
    velocityHistoryRef.current.push({ x: event.clientX, y: event.clientY, time: now })
    velocityHistoryRef.current = velocityHistoryRef.current.filter((s) => now - s.time <= MOMENTUM_VELOCITY_WINDOW_MS)

    const rawTransform: PannableCanvasTransform = {
      x: gesture.baseTransform.x + effectiveDx,
      y: gesture.baseTransform.y + effectiveDy,
      zoom: gesture.baseTransform.zoom,
    }

    const layer = layerRef.current
    const bounds = layer ? measureLayerContentBounds(layer) : null
    const viewportRect = getViewportRect()
    const nextTransform = applyRubberBandPan(rawTransform, bounds, viewportRect, RUBBER_BAND_RESISTANCE, pan, gesture.baseTransform)

    gestureRef.current = { ...gesture, started: true, moved: true }
    didDragRef.current = true
    scheduleTransformWrite(nextTransform)
    setIsPanning(true)
    event.preventDefault()
  }, [getDynamicMinZoom, getViewportRect, layerRef, onSemanticZoom, pan, scheduleTransformWrite, viewportRef, zoomAxis, zoomMode])

  const finishGesture = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    // Two-finger tap zoom out detection
    if (twoFingerStartRef.current) {
      const now = performance.now()
      const tap = twoFingerStartRef.current
      if (!tap.moved && now - tap.time < 300 && zoomAxis !== 'none') {
        twoFingerStartRef.current = null
        if (zoomMode === 'density' || zoomMode === 'none') {
          // no-op
        } else {
          const viewport = getViewportRect()
          if (zoomMode === 'semantic') {
            const width = Math.max(1, viewport?.width ?? window.innerWidth)
            const anchorRatio = Math.min(1, Math.max(0, (tap.midpoint.x - (viewport?.left ?? 0)) / width))
            onSemanticZoom?.(anchorRatio, 1.5)
          } else {
            const targetZoom = clampPannableCanvasZoom(transformRef.current.zoom / 1.5)
            const localTapMidpoint = {
              x: tap.midpoint.x - (viewport?.left ?? 0),
              y: tap.midpoint.y - (viewport?.top ?? 0),
            }
            const next = transformAboutPoint(transformRef.current, targetZoom, localTapMidpoint, zoomAxis)
            commitTransform(next)
          }
        }
        pointersRef.current.delete(event.pointerId)
        gestureRef.current = null
        setIsPanning(false)
        return
      }
      twoFingerStartRef.current = null
    }

    const gesture = gestureRef.current
    if (!gesture) {
      pointersRef.current.delete(event.pointerId)
      return
    }

    // Guard pinch -> pan transition so lifting one finger does not jump
    if (gesture.type === 'pinch') {
      pointersRef.current.delete(event.pointerId)
      const remainingTouch = [...pointersRef.current.entries()].find(
        ([id, p]) => id !== event.pointerId && p.pointerType === 'touch',
      )
      if (remainingTouch) {
        const [remainingId, remainingPointer] = remainingTouch
        gestureRef.current = {
          type: 'pan',
          pointerId: remainingId,
          startX: remainingPointer.x,
          startY: remainingPointer.y,
          baseTransform: transformRef.current,
          started: true,
          moved: true,
        }
        velocityHistoryRef.current = [{ x: remainingPointer.x, y: remainingPointer.y, time: performance.now() }]
        return
      }
      flushTransformWrite()
      springBackIfNeeded()
      if (gesture.moved) setPersistedTransform(normalizePannableCanvasTransform(transformRef.current))
      gestureRef.current = null
      setIsPanning(false)
      viewportRef.current?.removeAttribute('data-pannable-canvas-panning')
      return
    }

    if (gesture.pointerId !== event.pointerId) {
      pointersRef.current.delete(event.pointerId)
      return
    }

    pointersRef.current.delete(event.pointerId)
    flushTransformWrite()

    try {
      event.currentTarget.releasePointerCapture?.(event.pointerId)
    } catch {
      // Ignore
    }

    const viewport = viewportRef.current
    if (viewport) {
      viewport.removeAttribute('data-pannable-canvas-panning')
    }

    if (gesture.moved) {
      didDragRef.current = true
      if (viewport) {
        viewport.setAttribute('data-pannable-canvas-dragged', 'true')
      }
      window.setTimeout(() => {
        didDragRef.current = false
        viewportRef.current?.removeAttribute('data-pannable-canvas-dragged')
      }, 60)
    } else {
      didDragRef.current = false
      if (viewport) {
        viewport.removeAttribute('data-pannable-canvas-dragged')
      }
    }

    // Calculate momentum from the 100ms velocity window
    const now = performance.now()
    const samples = velocityHistoryRef.current
    let launchedMomentum = false
    if (gesture.moved && samples.length >= 2) {
      const oldest = samples[0]
      const dt = now - oldest.time
      if (dt > 10 && dt <= 150) {
        let vx = (event.clientX - oldest.x) / dt
        let vy = (event.clientY - oldest.y) / dt

        if (pan === 'none' || pan === 'y') vx = 0
        if (pan === 'none' || pan === 'x') vy = 0

        const speed = Math.hypot(vx, vy)
        const maxVelocity = 2.5
        if (speed > maxVelocity) {
          vx = (vx / speed) * maxVelocity
          vy = (vy / speed) * maxVelocity
        }

        if (speed > 0.08) {
          launchedMomentum = true
          startMomentum(vx, vy)
        }
      }
    }

    if (!launchedMomentum) {
      springBackIfNeeded()
      if (gesture.moved) setPersistedTransform(normalizePannableCanvasTransform(transformRef.current))
      gestureRef.current = null
      setIsPanning(false)
      if (pendingReconcileRef.current) {
        pendingReconcileRef.current = false
        reconcileDimensions()
      }
    }
  }, [commitTransform, flushTransformWrite, getViewportRect, onSemanticZoom, pan, reconcileDimensions, setPersistedTransform, springBackIfNeeded, startMomentum, viewportRef, zoomAxis, zoomMode])

  const onPointerUp = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    isNormalReleaseRef.current = true
    finishGesture(event)
  }, [finishGesture])

  const onPointerCancel = useCallback(() => {
    cancelGesture()
  }, [cancelGesture])

  const onLostPointerCapture = useCallback(() => {
    if (isNormalReleaseRef.current) {
      isNormalReleaseRef.current = false
      return
    }
    cancelGesture()
  }, [cancelGesture])

  const onClickCapture = useCallback((event: React.MouseEvent<HTMLElement>) => {
    if (didDragRef.current) {
      event.preventDefault()
      event.stopPropagation()
      didDragRef.current = false
    }
  }, [])

  const onContextMenuCapture = useCallback((event: React.MouseEvent<HTMLElement>) => {
    if (didDragRef.current) {
      event.preventDefault()
      event.stopPropagation()
    }
  }, [])

  return {
    transform,
    transformStyle: buildPannableCanvasTransformStyle(transform, zoomAxis),
    zoomIn,
    zoomOut,
    fitToContent,
    panBy,
    zoomAt,
    reconcileDimensions,
    isPanning,
    gestureHandlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel,
      onLostPointerCapture,
      onClickCapture,
      onContextMenuCapture,
    },
  }
}
